import React from 'react';
import { X, AlertTriangle, Clock } from 'lucide-react';
import { CEO_BRIEFING } from '../../data/ceoBriefing';
import { MARKETING_BRIEFING } from '../../data/marketingBriefing';
import { SALES_BRIEFING } from '../../data/salesBriefing';
import { SUPPORT_BRIEFING } from '../../data/supportBriefing';
import { PEOPLE_BRIEFING } from '../../data/peopleBriefing';
import { FINANCE_BRIEFING } from '../../data/financeBriefing';
import { AGENT_THUMBS } from './V4BriefingShared';

const TYPE_CONFIG = {
  task: { color: 'bg-blue-400' },
  meeting: { color: 'bg-violet-400' },
  approval: { color: 'bg-amber-400' },
};

const BRIEFING_BY_ROLE = {
  ceo: CEO_BRIEFING,
  marketing: MARKETING_BRIEFING,
  sales: SALES_BRIEFING,
  support: SUPPORT_BRIEFING,
  people: PEOPLE_BRIEFING,
  finance: FINANCE_BRIEFING,
};

const V4DailyTasksPanel = ({ isDark, onClose, activeUser }) => {
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/40' : 'text-slate-500';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';
  const divider = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const lineBg = isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]';
  const dotRing = isDark ? '#0a1628' : '#eee8df';

  const roleId = activeUser?.id;
  const briefing = BRIEFING_BY_ROLE[roleId];
  const timeline = briefing?.dailyTimeline || [];
  const hasTimeline = timeline.length > 0;

  // Footer alerts
  const risks = briefing?.risks;
  const leadAlert = briefing?.leadMetrics?.alert;
  const pipelineAlert = briefing?.pipelineMetrics?.alert;

  return (
    <div className="flex flex-col h-full" data-testid="v4-daily-tasks-panel">
      <div className={`flex items-center justify-between px-4 py-4 border-b ${divider}`}>
        <div>
          <p className={`text-sm font-semibold ${textPrimary}`}>My Day</p>
          <p className={`text-[11px] ${textSecondary}`}>{briefing?.greeting?.date || 'Monday, Jan 27'}</p>
        </div>
        <button onClick={onClose} className={`p-1 rounded-lg transition-colors ${hoverBg}`} data-testid="v4-daily-tasks-close">
          <X className={`w-4 h-4 ${textSecondary}`} />
        </button>
      </div>

      {hasTimeline ? (
        <>
          <div className="flex-1 overflow-y-auto px-4 py-3">
            {/* Legend */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-blue-400" />
                <span className={`text-[10px] ${textSecondary}`}>Task</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-violet-400" />
                <span className={`text-[10px] ${textSecondary}`}>Meeting</span>
              </div>
              {roleId === 'ceo' && (
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-amber-400" />
                  <span className={`text-[10px] ${textSecondary}`}>Approval</span>
                </div>
              )}
            </div>

            <div className="relative pl-5">
              <div className={`absolute left-[5px] top-1 bottom-1 w-px ${lineBg}`} />
              <div className="space-y-5">
                {timeline.map((item, i) => {
                  const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.task;
                  const agentThumb = AGENT_THUMBS[item.agent];
                  return (
                    <div key={i} className="relative" data-testid={`timeline-item-${i}`}>
                      <div
                        className={`absolute -left-5 top-0.5 w-[11px] h-[11px] rounded-full ${config.color}`}
                        style={{ boxShadow: `0 0 0 3px ${dotRing}` }}
                      />
                      <div>
                        <p className={`text-[10px] font-mono ${textSecondary}`}>{item.time}</p>
                        <p className={`text-xs font-medium mt-0.5 ${textPrimary}`}>{item.title}</p>
                        {item.agent && agentThumb && (
                          <div className="flex items-center gap-1.5 mt-1">
                            <img src={agentThumb} alt="" className="w-3.5 h-3.5 rounded-full object-cover" />
                            <span className={`text-[10px] capitalize ${textSecondary}`}>{item.agent}</span>
                          </div>
                        )}
                        {item.with && (
                          <p className={`text-[10px] mt-0.5 ${textSecondary}`}>{item.with} · {item.duration}</p>
                        )}
                        {item.count && (
                          <p className={`text-[10px] mt-0.5 ${textSecondary}`}>{item.count} items pending</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Footer — role-specific alerts */}
          <div className={`px-4 py-3 border-t ${divider} flex-shrink-0`}>
            {risks && risks.length > 0 && (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                  <p className={`text-[11px] font-medium ${textPrimary}`}>{risks.length} risks flagged</p>
                </div>
                {risks.map((risk) => (
                  <p key={risk.id} className={`text-[10px] mt-1 ${textSecondary}`}>
                    {risk.severity === 'critical' && <span className="text-red-400 font-bold mr-1">!</span>}
                    {risk.title}
                  </p>
                ))}
              </>
            )}
            {leadAlert && (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                  <p className={`text-[11px] font-medium ${textPrimary}`}>Lead alert</p>
                </div>
                <p className={`text-[10px] mt-1 ${textSecondary}`}>
                  {leadAlert.severity === 'high' && <span className="text-red-400 font-bold mr-1">!</span>}
                  {leadAlert.title}
                </p>
              </>
            )}
            {pipelineAlert && (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                  <p className={`text-[11px] font-medium ${textPrimary}`}>Pipeline alert</p>
                </div>
                <p className={`text-[10px] mt-1 ${textSecondary}`}>
                  {pipelineAlert.severity === 'high' && <span className="text-red-400 font-bold mr-1">!</span>}
                  {pipelineAlert.title}
                </p>
              </>
            )}
          </div>
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center">
            <Clock className={`w-8 h-8 mx-auto mb-2 ${textSecondary}`} />
            <p className={`text-xs ${textSecondary}`}>Daily task view for your role coming soon.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default V4DailyTasksPanel;
